import {
  Box,
  Dialog,
  Heading,
  HStack,
  Portal,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { RiftboundButton } from '../../../components/ui/button';
import { RiftboundSwitch } from '../../../components/ui/switch';
import {
  RiftboundBottomSheet,
  SHEET_Z_DECK_DIALOG,
} from '../../../components/ui/bottom-sheet';

export interface JoinTeamConsentSheetProps {
  open: boolean;
  teamName: string;
  shareCollection: boolean;
  onShareCollectionChange: (value: boolean) => void;
  busy?: boolean;
  onClose: () => void;
  onAccept: () => void;
  onJoinWithoutSharing: () => void;
}

/** Join-team consent — desktop dialog, mobile bottom sheet (matches app sheet patterns). */
export function JoinTeamConsentSheet({
  open,
  teamName,
  shareCollection,
  onShareCollectionChange,
  busy = false,
  onClose,
  onAccept,
  onJoinWithoutSharing,
}: JoinTeamConsentSheetProps) {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const title = `Join ${teamName}?`;

  const body = (
    <VStack align="stretch" gap={4}>
      <Text fontSize="sm" color="textMuted">
        Choose whether teammates can see your collection in the team aggregate.
      </Text>
      <HStack
        justify="space-between"
        gap={4}
        p={3}
        borderRadius="lg"
        borderWidth="hairline"
        borderColor="border"
        bg="bgElevated"
      >
        <Box>
          <Text fontWeight="semibold" fontSize="sm">
            Share collection with team
          </Text>
          <Text fontSize="xs" color="textMuted">
            You can change this later in team settings.
          </Text>
        </Box>
        <RiftboundSwitch
          checked={shareCollection}
          onCheckedChange={(e) => onShareCollectionChange(e.checked)}
          flexShrink={0}
        />
      </HStack>
    </VStack>
  );

  const footer = (
    <HStack
      gap={2}
      justify={isMobile ? 'stretch' : 'flex-end'}
      w="full"
      flexWrap={isMobile ? 'nowrap' : 'wrap'}
      flexDir={isMobile ? 'column-reverse' : 'row'}
    >
      <RiftboundButton
        size="sm"
        onClick={onClose}
        disabled={busy}
        flex={isMobile ? 1 : undefined}
        w={isMobile ? 'full' : undefined}
        minH={isMobile ? '44px' : undefined}
      >
        Cancel
      </RiftboundButton>
      <RiftboundButton
        size="sm"
        onClick={onJoinWithoutSharing}
        disabled={busy}
        flex={isMobile ? 1 : undefined}
        w={isMobile ? 'full' : undefined}
        minH={isMobile ? '44px' : undefined}
      >
        Join without sharing
      </RiftboundButton>
      <RiftboundButton
        size="sm"
        treatment="primary"
        loading={busy}
        onClick={onAccept}
        flex={isMobile ? 1 : undefined}
        w={isMobile ? 'full' : undefined}
        minH={isMobile ? '44px' : undefined}
      >
        {shareCollection ? 'Join & share' : 'Join'}
      </RiftboundButton>
    </HStack>
  );

  if (isMobile) {
    return (
      <RiftboundBottomSheet
        isOpen={open}
        onClose={onClose}
        zIndex={SHEET_Z_DECK_DIALOG}
        detent="content-height"
        disableDrag={busy}
        header={
          <Heading as="h2" size="sm" w="100%" textAlign="center" px={2}>
            {title}
          </Heading>
        }
      >
        <Box px={4} pb={4}>
          {body}
          <Box pt={4}>{footer}</Box>
        </Box>
      </RiftboundBottomSheet>
    );
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
      placement="center"
      closeOnInteractOutside={false}
    >
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.700" style={{ backdropFilter: 'blur(4px)' }} />
        <Dialog.Positioner>
          <Dialog.Content
            bg="surface"
            color="text"
            borderWidth="hairline"
            borderColor="border"
            mx={4}
            maxW="440px"
          >
            <Dialog.Header>
              <Dialog.Title asChild>
                <Heading as="h2" fontSize="lg" fontWeight="semibold">
                  {title}
                </Heading>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>{body}</Dialog.Body>
            <Dialog.Footer>{footer}</Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
